import React from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { Input, Button, Typography } from 'antd'
import { UserOutlined, MailOutlined, PhoneOutlined, EditFilled, CloseOutlined } from '@ant-design/icons'
import { UserCheck } from 'react-feather'
import { FlexRow, FlexColumn } from '../FlexboxReusables/FlexItems'

type PersonalInfoSegmentProps = {
  firstName: string
  lastName: string
  email: string
  phone: string
  position: string
  setFirstName: (firstName: string) => void
  setLastName: (lastName: string) => void
  setEmail: (email: string) => void
  setPhone: (phone: string) => void
  setPosition: (position: string) => void
  setEditPersonalInfo: (editPersonalInfo: boolean) => void
  editPersonalInfo: boolean
}

const PersonalInfoSegment: React.FC<PersonalInfoSegmentProps> = (
  {
    firstName,
    lastName,
    email,
    phone,
    position,
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    setPosition,
    setEditPersonalInfo,
    editPersonalInfo
  }: PersonalInfoSegmentProps) => {
  const intl = useIntl()

  return (
    <>
      <FlexRow width='100%' justifyContent='flex-end' alignItems='center'>
        <FlexRow justifyContent='flex-start'>
          <SubHeading level={4}>
            <FormattedMessage id='generic.personalData' />
          </SubHeading>
        </FlexRow>
        <EditButton type='primary' onClick={() => setEditPersonalInfo(!editPersonalInfo)}>
          {!editPersonalInfo ? <EditIcon /> : <EndEditIcon />}
          <FormattedMessage id='generic.edit' />
        </EditButton>
      </FlexRow>
  
      <FlexRow width='100%' justifyContent='space-evenly' alignItems='center' margin='0 0 2em 0'>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.firstName' />
          </Description>
          {editPersonalInfo ? (
            <StyledInput
              prefix={<UserOutlined />}
              placeholder={intl.formatMessage({ id: 'generic.firstName' })}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          ) : (
            <Typography.Text>{firstName}</Typography.Text>
          )}
        </FlexColumn>
  
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.lastName' />
          </Description>
          {editPersonalInfo ? (
            <StyledInput
              prefix={<UserOutlined />}
              placeholder={intl.formatMessage({ id: 'generic.lastName' })}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          ) : (
            <Typography.Text>{lastName}</Typography.Text>
          )}
        </FlexColumn>
      </FlexRow>
  
      <FlexRow width='100%' justifyContent='space-evenly' alignItems='center' margin='1em 0 2em 0'>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            Email
          </Description>
          {editPersonalInfo ? (
            <StyledInput
              prefix={<MailOutlined />}
              placeholder='Email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          ) : (
            <Typography.Text>{email}</Typography.Text>
          )}
        </FlexColumn>
  
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            <FormattedMessage id='generic.phone' />
          </Description>
          {editPersonalInfo ? (
            <StyledInput
              prefix={<PhoneOutlined />}
              placeholder={intl.formatMessage({ id: 'generic.phone' })}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          ) : (
            <Typography.Text>{phone}</Typography.Text>
          )}
        </FlexColumn>
      </FlexRow>
  
      <FlexRow width='100%' justifyContent='space-evenly' alignItems='center' margin='1em 0 2em 0'>
        <FlexColumn width='100%' alignItems='flex-start'>
          <Description>
            Position
          </Description>
          {editPersonalInfo ? (
            <StyledInput
              prefix={<UserCheck size={16} />}
              placeholder='Position'
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              style={{ width: '25%' }}
            />
          ) : (
            <Typography.Text>{position}</Typography.Text>
          )}
        </FlexColumn>
      </FlexRow>
    </>
  )
}

export default PersonalInfoSegment

const SubHeading = styled(Typography.Title)`
  margin: 0 0 1.5em 0 !important;
`

const Description = styled(Typography.Text)`
  font-size: 0.8em;
  color: grey;
`

const EditButton = styled(Button)`
  margin: 0 0 1.5em 0 !important;
`

const EditIcon = styled(EditFilled)`
  margin-right: 1em;
`

const EndEditIcon = styled(CloseOutlined)`
  margin-right: 1em;
`

const StyledInput = styled(Input)`
  width: 50%;

  @media (max-width: 768px) {
    width: 100%;
  }
`